import { glopicApi } from "@/lib/glopic-api";

export type DateRangePreset = "today" | "yesterday" | "7d" | "30d" | "week" | "month" | "quarter" | "custom";

export interface ReportDateRange {
  preset: DateRangePreset;
  timezone_name: string;
  date_from?: string;
  date_to?: string;
}

export interface RevenueCurrencyItem {
  currency: string;
  amount: number;
}

export interface KpiOverview {
  loginUsers: number;
  newUsers: number;
  newMembers: number;
  newRevenue: number;
  newRevenueByCurrency: RevenueCurrencyItem[];
  homeUv: number;
  generationSubmits: number;
  generationSuccess: number;
  generationFailed: number;
  generationBlocked: number;
  pointsConsumed: number;
  aiCostUsd: number;
  generationSuccessRate: number;
}

export interface TrendPoint {
  date: string;
  loginUsers: number;
  newUsers: number;
  homeUv: number;
  generationSubmits: number;
  generationSuccess: number;
  generationFailed: number;
  generationBlocked: number;
  revenueCny: number;
  revenueUsd: number;
}

export interface FunnelStep {
  eventName: string;
  label: string;
  uv: number;
  dropOff: number;
  stepRate: number;
  overallRate: number;
}

export interface EntryClickItem {
  entryId: string;
  entryName: string;
  exposureUv: number;
  clickUv: number;
  clickPv: number;
  ctr: number;
  generationSubmits: number;
  clickToSubmitRate: number;
}

export interface TabClickItem {
  tab: string;
  tabName: string;
  pageUv: number;
  clickUv: number;
  clickRate: number;
  submits: number;
  successes: number;
  failures: number;
  blocked: number;
  successRate: number;
}

export interface QualityTrendPoint {
  date: string;
  submit: number;
  success: number;
  failed: number;
  blocked: number;
}

export interface ErrorRankItem {
  reasonCode: string;
  failReason: string;
  modelId: string | null;
  mode: string | null;
  count: number;
  ratio: number;
  latestTime: string | null;
}

export interface ModelCostItem {
  modelId: string;
  provider: string | null;
  calls: number;
  successes: number;
  failures: number;
  successRate: number;
  pointsConsumed: number;
  aiCostUsd: number;
  avgCostUsd: number;
  avgPoints: number;
}

export interface EntryUsageCostSummary {
  deliveredImages: number;
  providerCalls: number;
  modelCalls: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  estimatedImageCostUsd: number;
  threeImageEstimateUsd: number | null;
  threeImageEstimateBasis: "recent_p50" | "model_config_fallback" | "unavailable";
  tokenCoverageRate: number;
}

export interface EntryUsageCostItem extends EntryUsageCostSummary {
  date?: string;
  sourceEntry: string;
  entryId: string;
  entryName: string;
  avgImageCostUsd?: number | null;
  sampleCount?: number;
}

export interface EntryUsageCostsResult {
  summary: EntryUsageCostSummary;
  dailyEntries: EntryUsageCostItem[];
  entrySummary: EntryUsageCostItem[];
}

export interface RecentErrorItem {
  genId: string;
  userId: string;
  modelId: string | null;
  mode: string | null;
  quality: string | null;
  taskStatus: string;
  reasonCode: string | null;
  failReason: string | null;
  pointsConsumed: number;
  createdAt: string | null;
}

export interface RecentErrorsResult {
  items: RecentErrorItem[];
  total: number;
  page: number;
  pageSize: number;
}

const pct = (value: unknown) => Number(((Number(value) || 0) * 100).toFixed(1));

type ApiRecord = Record<string, unknown>;

const unwrap = (response: { data: { data: unknown } }): ApiRecord => {
  const data = response.data.data;
  return data && typeof data === "object" ? (data as ApiRecord) : {};
};

const asArray = (value: unknown): ApiRecord[] =>
  Array.isArray(value) ? (value.filter((item) => item && typeof item === "object") as ApiRecord[]) : [];

export function resolveDateRange(preset: DateRangePreset): ReportDateRange {
  return {
    preset,
    timezone_name: "Asia/Shanghai",
  };
}

export async function fetchOverview(range: ReportDateRange): Promise<KpiOverview> {
  const data = unwrap(await glopicApi.get("/stats/reports/overview", { params: range }));
  return {
    loginUsers: Number(data.login_users ?? 0),
    newUsers: Number(data.new_users ?? 0),
    newMembers: Number(data.new_members ?? 0),
    newRevenue: Number(data.new_revenue ?? 0),
    newRevenueByCurrency: asArray(data.new_revenue_by_currency).map((item) => ({
      currency: String(item.currency ?? "UNKNOWN"),
      amount: Number(item.amount ?? 0),
    })),
    homeUv: Number(data.home_uv ?? 0),
    generationSubmits: Number(data.generation_submits ?? 0),
    generationSuccess: Number(data.generation_success ?? 0),
    generationFailed: Number(data.generation_failed ?? 0),
    generationBlocked: Number(data.generation_blocked ?? 0),
    pointsConsumed: Number(data.points_consumed ?? 0),
    aiCostUsd: Number(data.ai_cost_usd ?? 0),
    generationSuccessRate: pct(data.generation_success_rate),
  };
}

export async function fetchTrends(range: ReportDateRange): Promise<TrendPoint[]> {
  const data = unwrap(await glopicApi.get("/stats/reports/trends", { params: range }));
  return asArray(data.items).map((item) => ({
    date: String(item.date ?? ""),
    loginUsers: Number(item.login_users ?? 0),
    newUsers: Number(item.new_users ?? 0),
    homeUv: Number(item.home_uv ?? 0),
    generationSubmits: Number(item.generation_submits ?? 0),
    generationSuccess: Number(item.generation_success ?? 0),
    generationFailed: Number(item.generation_failed ?? 0),
    generationBlocked: Number(item.generation_blocked ?? 0),
    revenueCny: Number(item.revenue_cny ?? 0),
    revenueUsd: Number(item.revenue_usd ?? 0),
  }));
}

export async function fetchFunnel(range: ReportDateRange): Promise<FunnelStep[]> {
  const data = unwrap(await glopicApi.get("/stats/reports/funnel", { params: range }));
  return asArray(data.items).map((item) => ({
    eventName: String(item.event_name ?? ""),
    label: String(item.label ?? ""),
    uv: Number(item.uv ?? 0),
    dropOff: Number(item.drop_off ?? 0),
    stepRate: Number(item.step_rate ?? 0),
    overallRate: Number(item.overall_rate ?? 0),
  }));
}

export async function fetchEntryClicks(range: ReportDateRange): Promise<EntryClickItem[]> {
  const data = unwrap(await glopicApi.get("/stats/reports/entry-clicks", { params: range }));
  return asArray(data.items).map((item) => ({
    entryId: String(item.entry_id ?? ""),
    entryName: String(item.entry_name ?? ""),
    exposureUv: Number(item.exposure_uv ?? 0),
    clickUv: Number(item.click_uv ?? 0),
    clickPv: Number(item.click_pv ?? 0),
    ctr: Number(item.ctr ?? 0),
    generationSubmits: Number(item.generation_submits ?? 0),
    clickToSubmitRate: Number(item.click_to_submit_rate ?? 0),
  }));
}

export async function fetchTabClicks(range: ReportDateRange): Promise<TabClickItem[]> {
  const data = unwrap(await glopicApi.get("/stats/reports/tab-clicks", { params: range }));
  return asArray(data.items).map((item) => ({
    tab: String(item.tab ?? ""),
    tabName: String(item.tab_name ?? ""),
    pageUv: Number(item.page_uv ?? 0),
    clickUv: Number(item.click_uv ?? 0),
    clickRate: Number(item.click_rate ?? 0),
    submits: Number(item.submits ?? 0),
    successes: Number(item.successes ?? 0),
    failures: Number(item.failures ?? 0),
    blocked: Number(item.blocked ?? 0),
    successRate: Number(item.success_rate ?? 0),
  }));
}

export async function fetchGenerationQuality(range: ReportDateRange) {
  const data = unwrap(await glopicApi.get("/stats/reports/generation-quality", { params: range }));
  return {
    trend: asArray(data.trend).map((item) => ({
      date: String(item.date ?? ""),
      submit: Number(item.submit ?? 0),
      success: Number(item.success ?? 0),
      failed: Number(item.failed ?? 0),
      blocked: Number(item.blocked ?? 0),
    })) as QualityTrendPoint[],
    errorTop: asArray(data.error_top).map((item) => ({
      reasonCode: String(item.reason_code ?? "UNKNOWN"),
      failReason: String(item.failure_message ?? "生成失败"),
      modelId: item.model_id ? String(item.model_id) : null,
      mode: item.mode ? String(item.mode) : null,
      count: Number(item.count ?? 0),
      ratio: Number(item.ratio ?? 0),
      latestTime: item.latest_time ? String(item.latest_time) : null,
    })) as ErrorRankItem[],
  };
}

export async function fetchModelCosts(range: ReportDateRange): Promise<ModelCostItem[]> {
  const data = unwrap(await glopicApi.get("/stats/reports/model-costs", { params: range }));
  return asArray(data.items).map((item) => ({
    modelId: String(item.model_id ?? "unknown"),
    provider: item.provider ? String(item.provider) : null,
    calls: Number(item.calls ?? 0),
    successes: Number(item.successes ?? 0),
    failures: Number(item.failures ?? 0),
    successRate: Number(item.success_rate ?? 0),
    pointsConsumed: Number(item.points_consumed ?? 0),
    aiCostUsd: Number(item.ai_cost_usd ?? 0),
    avgCostUsd: Number(item.avg_cost_usd ?? 0),
    avgPoints: Number(item.avg_points ?? 0),
  }));
}

const usageCostSummary = (item: ApiRecord): EntryUsageCostSummary => ({
  deliveredImages: Number(item.delivered_images ?? 0),
  providerCalls: Number(item.provider_calls ?? 0),
  modelCalls: Number(item.model_calls ?? 0),
  inputTokens: Number(item.input_tokens ?? 0),
  outputTokens: Number(item.output_tokens ?? 0),
  cachedInputTokens: Number(item.cached_input_tokens ?? 0),
  reasoningTokens: Number(item.reasoning_tokens ?? 0),
  totalTokens: Number(item.total_tokens ?? 0),
  estimatedImageCostUsd: Number(item.estimated_image_cost_usd ?? 0),
  threeImageEstimateUsd:
    item.three_image_estimate_usd === null || item.three_image_estimate_usd === undefined
      ? null
      : Number(item.three_image_estimate_usd),
  threeImageEstimateBasis: ["recent_p50", "model_config_fallback"].includes(
    String(item.three_image_estimate_basis),
  )
    ? (String(item.three_image_estimate_basis) as "recent_p50" | "model_config_fallback")
    : "unavailable",
  tokenCoverageRate: Number(item.token_coverage_rate ?? 0),
});

const usageCostItem = (item: ApiRecord): EntryUsageCostItem => ({
  ...usageCostSummary(item),
  date: item.date ? String(item.date) : undefined,
  sourceEntry: String(item.source_entry ?? "unknown"),
  entryId: String(item.entry_id ?? "unknown"),
  entryName: String(item.entry_name ?? item.entry_id ?? "未知入口"),
  avgImageCostUsd:
    item.avg_image_cost_usd === null || item.avg_image_cost_usd === undefined
      ? null
      : Number(item.avg_image_cost_usd),
  sampleCount: Number(item.sample_count ?? 0),
});

export async function fetchEntryUsageCosts(
  range: ReportDateRange,
  sourceEntry = "consumer_app",
): Promise<EntryUsageCostsResult> {
  const data = unwrap(
    await glopicApi.get("/stats/reports/entry-costs", {
      params: { ...range, source_entry: sourceEntry },
    }),
  );
  const summary =
    data.summary && typeof data.summary === "object" ? (data.summary as ApiRecord) : {};
  return {
    summary: usageCostSummary(summary),
    dailyEntries: asArray(data.daily_entries).map(usageCostItem),
    entrySummary: asArray(data.entry_summary).map(usageCostItem),
  };
}

export async function fetchRecentErrors(
  range: ReportDateRange,
  page: number,
  pageSize: number,
  reasonCode?: string,
): Promise<RecentErrorsResult> {
  const data = unwrap(
    await glopicApi.get("/stats/reports/recent-errors", {
      params: { ...range, page, page_size: pageSize, reason_code: reasonCode || undefined },
    }),
  );
  return {
    items: asArray(data.items).map((item) => ({
      genId: String(item.gen_id ?? ""),
      userId: String(item.user_id ?? ""),
      modelId: item.model_id ? String(item.model_id) : null,
      mode: item.mode ? String(item.mode) : null,
      quality: item.quality ? String(item.quality) : null,
      taskStatus: String(item.task_status ?? ""),
      reasonCode: item.reason_code ? String(item.reason_code) : null,
      failReason: item.failure_message ? String(item.failure_message) : null,
      pointsConsumed: Number(item.points_consumed ?? 0),
      createdAt: item.created_at ? String(item.created_at) : null,
    })),
    total: Number(data.total ?? 0),
    page: Number(data.page ?? page),
    pageSize: Number(data.page_size ?? pageSize),
  };
}
